import React, {useEffect} from 'react';
import {useState} from 'react';
import {View, ScrollView, TouchableOpacity, Image, StyleSheet} from 'react-native';
import Text from '../../../components/Text';
import Icon from '@expo/vector-icons/Feather';
import DisabledInput from '../../../components/DisabledInput';
import LoadingSpinner from '../../../components/LoadingSpinner';
import Navbar from '../../../components/Navbar';
import Button from '../../../components/Button';
import TextArea from '../../../components/TextArea';
import Checkbox from '../../../components/Checkbox';
import {Unit} from '../types';
import {fetchUnitById, updateUnit} from '../../../api/units';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../../service/flashMessage';
import DateTimeInput from '../../../components/DateTimeInput';
import Container from '../../../components/Container';
import AsterixSwitch from '../../../components/AsterixSwitch';
import FormContainer from '../../../components/FormContainer';

const EditUnit = ({navigation, route}) => {
  const [unit, setUnit] = useState<Unit>(route.params.unit);
  const [loadingUnit, setLoadingUnit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateDates = () => {
    const startDate = buildDate(unit.startBlockAutomatedCalls);
    const endDate = buildDate(unit.endBlockautomatedCalls);

    if (!startDate || !endDate) {
      return false;
    }

    return startDate.getTime() > endDate.getTime();
  };

  const submitData = async () => {
    const datesValid = validateDates();
    if (!!unit.allowAutomatedCalls && !datesValid) {
      showErrorMessage('EditUnit: Calls stop time needs to be greater than start time ');
      return;
    }

    setIsSubmitting(true);
    try {
      await updateUnit(unit.id, {
        specialInstructions: unit.specialInstructions,
        startBlockAutomatedCalls: unit.startBlockAutomatedCalls,
        endBlockautomatedCalls: unit.endBlockautomatedCalls,
        allowAutomatedCalls: unit.allowAutomatedCalls,
      });
      showSuccessMessage('Unit information updated successfully');
      navigation.goBack();
    } catch (error) {
      showErrorMessage('EditUnit - 2: ' + error.message);
    }
    setIsSubmitting(false);
  };

  const fetchUnit = async (id: string) => {
    setLoadingUnit(true);
    try {
      const res = await fetchUnitById(id);
      setUnit(res);
    } catch (error) {
      // showErrorMessage(error.message);
    } finally {
      setLoadingUnit(false);
    }
  };

  useEffect(() => {
    fetchUnit(unit.id);
  }, [unit.id]);

  const buildDate = (time: string | null | undefined) => {
    if (typeof time !== 'string' || !time.trim()) {
      return null;
    }

    const parts = time.split(':');
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt((parts[1] ?? '0').split(':')[0], 10);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return null;
    }

    const today = new Date();
    const date = today.setHours(hours, minutes);
    return new Date(date);
  };

  return (
    <Container>
      <Navbar navigation={navigation} title={'Edit unit'} />
      <FormContainer>
        {loadingUnit ? (
          <LoadingSpinner />
        ) : (
          <ScrollView
            contentContainerStyle={styles.scrollViewContent}>
            {/* <View style={styles.row}>
              <Text style={styles.checkBoxLabel}>Allow automated calls ?</Text>
              <AsterixSwitch
                value={Boolean(unit.allowAutomatedCalls)}
                onValueChange={value => {
                  setUnit({...unit, allowAutomatedCalls: value});
                }}
              />
            </View> */}

            {!!unit.allowAutomatedCalls && (
              <>
                <DateTimeInput
                  label="Call restriction start time: *"
                  value={
                    !!unit.startBlockAutomatedCalls
                      ? buildDate(unit.startBlockAutomatedCalls)
                      : null
                  }
                  onConfirm={time => {
                    if (typeof time === 'string') {
                      setUnit({
                        ...unit,
                        startBlockAutomatedCalls: time,
                      });
                    }
                  }}
                  mode="time"
                  description="Time to STOP automated system calls in the evening"
                />
                <DateTimeInput
                  label="Call restriction stop time: *"
                  value={
                    !!unit.endBlockautomatedCalls
                      ? buildDate(unit.endBlockautomatedCalls)
                      : null
                  }
                  onConfirm={time => {
                    if (typeof time === 'string') {
                      setUnit({
                        ...unit,
                        endBlockautomatedCalls: time,
                      });
                    }
                  }}
                  mode="time"
                  description="Time to RE-START system automatic calls in the morning"
                />
              </>
            )}
            <>
              <>
                <Text style={styles.textInputLabel}>
                  Special Instructions *
                </Text>
                <TextArea
                  placeholder="Enter special instructions"
                  value={
                    unit.specialInstructions ? unit.specialInstructions : ''
                  }
                  onChangeText={text =>
                    setUnit({...unit, specialInstructions: text})
                  }
                  style={styles.textArea}
                  totalLines={4}
                  autoCompleteType=""
                />
              </>
              <DisabledInput title="Unit name" value={unit.unit} />
              <DisabledInput title="Unit display" value={unit.unitDisplay} />
              <View>
                <Text style={styles.textInputLabel}>Unit picture*</Text>
                <TouchableOpacity style={styles.pictureUploadContainer}>
                  {unit?.picture ? (
                    <Image
                      source={{
                        uri: `data:image/jpeg;base64,${unit?.picture}`,
                      }}
                      style={styles.picture}
                    />
                  ) : (
                    <Icon
                      name="image"
                      size={24}
                      style={styles.pictureUploadIcon}
                    />
                  )}
                </TouchableOpacity>
              </View>
              <DisabledInput
                title="Site"
                value={unit.unit ? unit.unit : unit.unitDisplay}
              />
              <DisabledInput title="Community" value={unit.communityName} />
              <DisabledInput title="Building" value={unit.buildingName} />
              <DisabledInput title="Status" value={unit.status} />
              <DisabledInput
                title="Enter addressLine1"
                value={unit.addressLine1}
              />
              <DisabledInput
                title="Enter addressLine2"
                value={unit.addressLine2}
              />
              <DisabledInput title="City" value={unit?.city} />
              <DisabledInput title="State" value={unit?.state} />
              <DisabledInput title="Zip" value={unit?.zip} />
              <DisabledInput title="Floor" value={unit?.floor} />
              <DisabledInput title="Line" value={unit?.line} />
              <DisabledInput
                title="Account Number"
                value={unit.accountNumber}
              />
              <View style={styles.row}>
                <Text style={styles.checkBoxLabel}>Community Delinquent ?</Text>
                <Checkbox
                  style={styles.checkbox}
                  isDisabled
                  value={unit.communityDelinquent}
                  aria-label="Community Delinquent"
                />
              </View>
              <View style={styles.row}>
                <Text style={styles.checkBoxLabel}>Building Delinquent ?</Text>
                <Checkbox
                  isDisabled
                  value={unit.buildingDelinquent}
                  aria-label="Building Delinquent"
                />
              </View>
              <DisabledInput title="Directions" value={unit.directions} />
            </>
          </ScrollView>
        )}
        <Button
          onPress={submitData}
          isLoading={isSubmitting}
          py={4}
          mb={24}
          mx={2}
          isLoadingText="Submitting ...">
          Save
        </Button>
      </FormContainer>
    </Container>
  );
};

const styles = StyleSheet.create({
  scrollViewContent: {
    paddingHorizontal: 8,
    marginBottom: 60,
    paddingBottom: 20,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 8,
  },
  checkBoxLabel: {
    // Add your styles here
  },
  textInputLabel: {
    // Add your styles here
  },
  textArea: {
    borderColor: 'green',
    borderRadius: 4,
  },
  pictureUploadContainer: {
    // Add your styles here
  },
  picture: {
    borderRadius: 4,
    width: 128,
    height: 96,
  },
  pictureUploadIcon: {
    // Add your styles here
  },
  checkbox: {
    marginTop: 8,
  },
});

export default EditUnit;
